import { IMatchedBusiness } from "@faire/web-api/indigofair/data/IMatchedBusiness";
import { CANADA_PROVINCE } from "@faire/web/common/consts/CANADA_PROVINCE";
import { CANADA_PROVINCE_SHORTHAND } from "@faire/web/common/consts/CANADA_PROVINCE_SHORTHAND";
import { STATE_PROVINCE_SHORTHAND_TO_NAME } from "@faire/web/common/consts/STATE_PROVINCE_SHORTHAND_TO_NAME";
import { THREE_LETTER_PRIMARY_COUNTRY_CODES } from "@faire/web/common/consts/THREE_LETTER_PRIMARY_COUNTRY_CODES";
import { US_STATE } from "@faire/web/common/consts/US_STATE";
import { US_STATE_SHORTHAND } from "@faire/web/common/consts/US_STATE_SHORTHAND";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Input } from "@faire/web/slate/Input";
import { IOption, SearchDropdown } from "@faire/web/slate/Input/SearchDropdown";
import React from "react";

import { SampleForm } from "@faire/retailer/features/ProspectingWithSamples/ProspectingSampleSignupFlow";

export const StateOrProvince = ({
  shouldRender,
  label,
  country,
  error,
  value,
  handleChange,
  setFormValues,
  setFieldValue,
  formValues,
}: {
  shouldRender: boolean;
  label: string;
  country: string;
  error?: string | boolean;
  value: string | undefined;
  handleChange: (e: React.ChangeEvent<HTMLInputElement> | IOption) => void;
  setFieldValue: (field: string, value: string | IMatchedBusiness) => void;
  setFormValues: (formValues: SampleForm) => void;
  formValues: SampleForm;
}) => {
  const { strictLocalize } = useStrictLocalization();
  if (!shouldRender) {
    return null;
  }

  const hasStateOrProvince = (
    [
      THREE_LETTER_PRIMARY_COUNTRY_CODES.USA,
      THREE_LETTER_PRIMARY_COUNTRY_CODES.CAN,
    ] as string[]
  ).includes(country);

  if (hasStateOrProvince) {
    return (
      <SearchDropdown
        label={label}
        data-test-id="state"
        name="state"
        value={
          STATE_PROVINCE_SHORTHAND_TO_NAME(strictLocalize)[
            value as keyof ReturnType<typeof STATE_PROVINCE_SHORTHAND_TO_NAME>
          ] ?? value
        }
        error={error}
        options={getStateOrProvince(country)(strictLocalize)}
        alternativeOptions={getStateOrProvinceShorthand(country)}
        onChange={handleChange}
        onSelect={(stateOrProvince) => {
          setFieldValue("state", stateOrProvince.value);
          setFormValues({ ...formValues, state: stateOrProvince.value });
        }}
      />
    );
  }

  return (
    <Input
      label={label}
      type="text"
      name="state"
      error={error}
      value={value}
      onChange={handleChange}
    />
  );
};

const getStateOrProvince = (country: string | undefined) => {
  switch (country) {
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.CAN:
      return CANADA_PROVINCE;
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.USA:
    default:
      return US_STATE;
  }
};

const getStateOrProvinceShorthand = (country: string | undefined): string[] => {
  switch (country) {
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.CAN:
      return CANADA_PROVINCE_SHORTHAND;
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.USA:
    default:
      return US_STATE_SHORTHAND;
  }
};
